import type { SpeicherPhase } from './gbuModulLogic';

/**
 * Die Warteschlange des automatischen Speicherns — ohne React, damit sie
 * ohne jsdom pruefbar ist (useAutoSpeichern ist nur die Huelle darum).
 *
 * Regeln (Hinweise von Paket C):
 *   - NIE ZWEI LAEUFE GLEICHZEITIG je Einheit. Ein neuer Lauf haengt sich an
 *     den laufenden an und schreibt danach den NEUESTEN Stand — die
 *     optimistische Sperre des Kopfs, Kind-Zeilen ohne Revision (letzter
 *     Schreiber gewinnt) und neue Zeilen (erst die Id abwarten) brauchen das.
 *   - Ob geschrieben werden muss, sagt `zuTun()`: der Entwurf gegen den
 *     zuletzt BESTAETIGTEN Stand (sofort nach jeder Antwort gesetzt). `geaendert`
 *     aus dem letzten Zeichnen kann direkt nach einer Antwort noch veraltet
 *     sein (React hat noch nicht neu gezeichnet) — es entscheidet nur, ob
 *     eine nicht speicherbare Eingabe offen ist.
 *   - Nichts zu tun → true (false nur, wenn eine unvollstaendige Eingabe offen
 *     ist); zu tun, aber nicht bereit → false ohne Schreiben.
 *   - true heisst „ALLES gespeichert": wurde waehrend des Schreibens weiter
 *     getippt (die GENERATION des Entwurfs hat sich seit Rundenbeginn
 *     geaendert), wird erneut geschrieben — hoechstens SPEICHER_RUNDEN_MAX
 *     Runden. Ist der Entwurf inzwischen ungueltig oder kommt er nicht zur
 *     Ruhe → false.
 *   - `warte()` wartet nur auf den laufenden Lauf, ohne einen neuen zu starten
 *     (Verwerfen einer neuen Zeile: erst die Antwort, dann aufraeumen).
 */
export interface SpeicherLauf {
  /** Speichern (angehaengt) und das Ergebnis liefern: true = danach nichts mehr offen. */
  lauf: () => Promise<boolean>;
  /** Auf den gerade laufenden Lauf warten (keiner: sofort). */
  warte: () => Promise<void>;
  laeuft: () => boolean;
}

export interface SpeicherLaufOptionen {
  /**
   * Der Stand aus dem letzten Festschreiben: `geaendert` (Anzeige, kann kurz
   * veraltet sein), `bereit` (speicherbar) und `generation` — aendert sich
   * mit jeder Eingabe (z. B. Fingerabdruck des Entwurfs).
   */
  stand: () => { geaendert: boolean; bereit: boolean; generation: string };
  /** Muss geschrieben werden? Neuester Entwurf gegen den zuletzt BESTAETIGTEN Stand — nie veraltet. */
  zuTun: () => boolean;
  /** Schreibt den neuesten Entwurf und setzt danach SOFORT den bestaetigten Stand (prueft selbst, nie doppelt). */
  speichere: () => Promise<void>;
  /** Phase fuer die Anzeige; Fehlertext als Klartext. */
  onPhase: (phase: SpeicherPhase, fehler: string | null) => void;
  fehlerText: (e: unknown) => string;
}

/** Hoechstens so viele Schreibrunden je Lauf, wenn waehrend des Schreibens weiter getippt wird. */
export const SPEICHER_RUNDEN_MAX = 5;

export const erzeugeSpeicherLauf = (o: SpeicherLaufOptionen): SpeicherLauf => {
  let aktuell: Promise<boolean> | null = null;

  /** Ohne Schreiben: offen ist nur eine (noch) nicht speicherbare Eingabe. */
  const ohneSchreiben = (s: { geaendert: boolean; bereit: boolean }): boolean => !(s.geaendert && !s.bereit);

  const lauf = async (): Promise<boolean> => {
    // Anhaengen statt parallel: erst den laufenden abwarten, dann mit dem neuesten Stand.
    while (aktuell) await aktuell;
    const vorher = o.stand();
    if (!o.zuTun()) return ohneSchreiben(vorher);
    if (!vorher.bereit) return false;
    const dieser = (async () => {
      let s = vorher;
      for (let runde = 1; ; runde++) {
        o.onPhase('speichert', null);
        try {
          await o.speichere();
        } catch (e) {
          o.onPhase('fehler', o.fehlerText(e));
          return false;
        }
        o.onPhase('gespeichert', null);
        const nach = o.stand();
        // Waehrend des Schreibens nichts getippt: alles bestaetigt.
        if (nach.generation === s.generation) return true;
        // Weiter getippt: der neue Stand entscheidet (bestaetigt ist schon gesetzt — keine Schleife aus alten Refs).
        if (!o.zuTun()) return ohneSchreiben(nach);
        if (!nach.bereit) return false;
        // Kommt nicht zur Ruhe: offen melden — die Entprellung holt den Rest nach.
        if (runde >= SPEICHER_RUNDEN_MAX) return false;
        s = nach;
      }
    })();
    aktuell = dieser;
    try {
      return await dieser;
    } finally {
      if (aktuell === dieser) aktuell = null;
    }
  };

  const warte = async (): Promise<void> => {
    while (aktuell) await aktuell;
  };

  return { lauf, warte, laeuft: () => aktuell !== null };
};

/** So lange warten „Zur Freigabe", Vorschau, Ausgabe und Zurueck hoechstens auf ausstehende Speicherungen. */
export const SPEICHER_ZEITLIMIT_MS = 10_000;

/**
 * Ein Versprechen mit Zeitlimit: haengt ein Speichern (Netz), soll „Zur
 * Freigabe" bzw. Zurueck nicht still warten, sondern nach `ms` mit
 * `zeitUeberschritten` zurueckkommen. Das Versprechen selbst laeuft weiter.
 */
export const mitZeitlimit = <T>(p: Promise<T>, ms: number, ersatz: T): Promise<{ wert: T; zeitUeberschritten: boolean }> =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve({ wert: ersatz, zeitUeberschritten: true }), ms);
    p.then(
      (wert) => { clearTimeout(t); resolve({ wert, zeitUeberschritten: false }); },
      (e: unknown) => { clearTimeout(t); reject(e); },
    );
  });
